import { useTranslation } from 'react-i18next'
import { Receipt } from 'lucide-react'
import clsx from 'clsx'
import { useContentFeeOrders } from '@/hooks/admin/useContentFee'
import { learnerHandle } from '@/utils/learnerEmail'
import type { ContentFeeOrderRow } from '@/api/contentFee'

function statusClass(status: string): string {
  switch (status) {
    case 'active':
      return 'bg-green-50 text-green-700 border border-green-200'
    case 'refunded':
      return 'bg-red-50 text-red-700 border border-red-200'
    default:
      return 'bg-gray-100 text-gray-600 border border-gray-200'
  }
}

export default function OrdersPage() {
  const { t, i18n } = useTranslation('admin')
  const { data: orders = [], isLoading } = useContentFeeOrders()
  const locale = i18n.language === 'en' ? 'en-US' : 'ko-KR'

  const formatAmount = (amount: number | null) =>
    amount != null ? amount.toLocaleString(locale) : t('ordersPage.amountUnknown')
  const formatDate = (iso: string | null) => {
    if (!iso) return t('ordersPage.dateUnknown')
    const d = new Date(iso)
    if (Number.isNaN(d.getTime())) return t('ordersPage.dateUnknown')
    return d.toLocaleString(locale, { timeZone: 'Asia/Seoul' })
  }
  const statusLabel = (status: string) =>
    t(`ordersPage.status.${status}`, { defaultValue: status })
  const formatCardLast4 = (last4: string | null) => (last4 ? `**** ${last4}` : '—')
  const formatOrderId = (orderId: number | null) => orderId?.toString() ?? '—'
  const formatBeneficiaryFallback = (userId: number) =>
    t('ordersPage.beneficiaryFallback', { userId })

  return (
    <div className="space-y-4 py-4">
      <div>
        <h1 className="text-lg font-semibold text-gray-900">{t('ordersPage.title')}</h1>
        <p className="text-sm text-gray-500">{t('ordersPage.subtitle')}</p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">{t('ordersPage.loading')}</p>
      ) : orders.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-24 text-gray-400">
          <Receipt className="w-12 h-12 mb-4" />
          <p className="text-sm">{t('ordersPage.empty')}</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-gray-200">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50 text-gray-500">
              <tr>
                <th className="px-4 py-2 text-left font-medium">{t('ordersPage.col.orderId')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('ordersPage.col.beneficiary')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('ordersPage.col.level')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('ordersPage.col.granted')}</th>
                <th className="px-4 py-2 text-right font-medium">{t('ordersPage.col.amount')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('ordersPage.col.card')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('ordersPage.col.status')}</th>
                <th className="px-4 py-2 text-left font-medium">{t('ordersPage.col.paidAt')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white">
              {orders.map((row: ContentFeeOrderRow) => (
                <tr key={row.entitlementId} className="text-gray-800">
                  <td className="px-4 py-2 font-mono text-xs text-gray-600">{formatOrderId(row.orderId)}</td>
                  <td className="px-4 py-2">
                    <div className="font-medium text-gray-900">
                      {row.beneficiaryName ?? formatBeneficiaryFallback(row.beneficiaryUserId)}
                    </div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {row.beneficiaryEmail
                        ? learnerHandle(row.beneficiaryEmail)
                        : formatBeneficiaryFallback(row.beneficiaryUserId)}
                    </div>
                  </td>
                  <td className="px-4 py-2">{row.level}</td>
                  <td className="px-4 py-2 text-right">{row.grantedLessons}</td>
                  <td className="px-4 py-2 text-right">{formatAmount(row.amount)}</td>
                  <td className="px-4 py-2">{formatCardLast4(row.cardLast4Digits)}</td>
                  <td className="px-4 py-2">
                    <div className="flex flex-col items-start gap-1">
                      <span className={clsx('inline-flex rounded-full px-2 py-0.5 text-xs', statusClass(row.status))}>
                        {statusLabel(row.status)}
                      </span>
                      {row.status === 'refunded' && row.refundedAt ? (
                        <span className="text-xs text-gray-500">
                          {formatDate(row.refundedAt)}
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td className="px-4 py-2">{formatDate(row.paidAt)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
